// "Will it fit?" facts for a product page, computed from the item's own sizes
// with the SAME rules the free calculators run (scripts/sizing-rules.mjs).
// One implementation, shared by the React PDP (src/components/pdp/FitFacts.tsx)
// and the prerendered product body (scripts/prerender-body.mjs), so a crawler
// and a shopper read identical numbers.
//
// Inputs are the supplier's structured "Item Width/Depth/Height" fields (and a
// stool's seat height), or a rug's size from its title. Nothing is guessed:
// a product whose sizes are missing or implausible gets no facts at all.

import {
  BED_RUGS,
  CHAIR_CLEARANCE_IN,
  END_TABLE_ARM_TOLERANCE_IN,
  LAMP_SURFACES,
  TABLE_LAMP_TOTAL_IN,
  MIRROR_GAP_IN,
  NIGHTSTAND_MATTRESS_TOLERANCE_IN,
  PATH_CLEARANCE_IN,
  RECT_TABLES,
  ROUND_TABLES,
  STOOL_CLASSES,
  STOOL_SPACING_IN,
  artWidthOverFurniture,
  chandelierOverTableIn,
  coffeeTableSize,
  diningRug,
  rugLabel,
} from "./sizing-rules.mjs";

// ---------------------------------------------------------------------------
// Specs
// ---------------------------------------------------------------------------

/** Product types whose structured sizes are kept in the product cache. */
export const SPEC_TYPES =
  /^(Sofas|Love Seats|Sectionals|Bar Chairs|Coffee Tables|Chandeliers|Wall Art|Dining Tables|End-Side Tables|Nightstands & Bedside Tables|Mirrors|Table Lamps|Dressers|Console Tables|Sideboards)$/;

/**
 * Item width, depth and height ("Item Depth: 38.00 Inches") and a stool's seat
 * height ("Seat Height - Floor to Seat 24.25-29.75", "a seat height of 30
 * inches"), from the plain-text description. These sit past the 400-character
 * description cap, so the build keeps them separately.
 */
export function productSpecs(text, productType) {
  if (!SPEC_TYPES.test(productType || "")) return undefined;
  const t = String(text ?? "");
  const n = (re) => {
    const m = t.match(re);
    return m ? Number(m[1]) : undefined;
  };
  const specs = {
    w: n(/Item Width:\s*([\d.]+)/i),
    d: n(/Item Depth:\s*([\d.]+)/i),
    h: n(/Item Height:\s*([\d.]+)/i),
  };
  const seat = t.match(/Seat Height - Floor to Seat\s*([\d.]+)(?:\s*-\s*([\d.]+))?/i) || t.match(/seat height of\s*([\d.]+)/i);
  if (seat) specs.seat = seat[2] ? [Number(seat[1]), Number(seat[2])] : [Number(seat[1]), Number(seat[1])];
  for (const k of Object.keys(specs)) if (specs[k] === undefined || Number.isNaN(specs[k])) delete specs[k];
  return Object.keys(specs).length ? specs : undefined;
}

/** A rug's nominal size from its title: 8' X 10' …, 8' … Round …; runners and odd formats give null. */
export function rugSize(title) {
  const t = String(title ?? "");
  const rect = t.match(/^(\d+(?:\.\d+)?)'\s*X\s*(\d+(?:\.\d+)?)'/i);
  if (rect) {
    const [a, b] = [Number(rect[1]), Number(rect[2])].sort((x, y) => x - y);
    return { shape: "rect", w: a, l: b };
  }
  const single = t.match(/^(\d+(?:\.\d+)?)'\s/);
  if (single && /\bround\b/i.test(t) && !/\brunner\b/i.test(t)) return { shape: "round", d: Number(single[1]) };
  return null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (n) => String(Math.round(n * 10) / 10);
const between = (v, lo, hi) => v >= lo && v <= hi;

/** A title that leads with a size (113" Sofa…) must name one of the parsed axes. */
function titleAgrees(title, values) {
  const lead = String(title ?? "").match(/^(\d+(?:\.\d+)?)\s*"/);
  if (!lead) return true;
  const t = Number(lead[1]);
  return values.some((v) => v > 0 && Math.abs(v - t) <= 1);
}

/** The [min, max] of `candidates` for which `test` holds, or null. */
function span(candidates, test) {
  const hits = candidates.filter(test);
  return hits.length ? [hits[0], hits[hits.length - 1]] : null;
}
const range = (lo, hi) => Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);

const TOOLS = {
  rug: { href: "/tools/rug-size-calculator", label: "Rug size calculator" },
  door: { href: "/tools/sofa-door-fit-calculator", label: "Sofa door fit calculator" },
  coffee: { href: "/tools/coffee-table-size-calculator", label: "Coffee table size calculator" },
  stool: { href: "/tools/bar-stool-height-calculator", label: "Bar stool height calculator" },
  chandelier: { href: "/tools/chandelier-size-calculator", label: "Chandelier size calculator" },
  art: { href: "/tools/wall-art-size-calculator", label: "Wall art size calculator" },
  dining: { href: "/tools/dining-table-size-calculator", label: "Dining table size calculator" },
};
const GUIDES = {
  endTable: { href: "/ideas/end-table-height-guide", label: "End table height guide" },
  nightstand: { href: "/ideas/nightstand-height-guide", label: "Nightstand height guide" },
  mirror: { href: "/ideas/mirror-size-guide", label: "Mirror size guide" },
  tableLamp: { href: "/ideas/table-lamp-size-guide", label: "Table lamp size guide" },
};

// ---------------------------------------------------------------------------
// Per type
// ---------------------------------------------------------------------------

function rugFacts(title) {
  const size = rugSize(title);
  if (!size) return null;
  const facts = [];
  if (size.shape === "rect") {
    const { w, l } = size;
    if (w < 5 || l > 20) return null;
    facts.push(`Living room: it fits a room of ${fmt(w + 3)} × ${fmt(l + 3)} ft or larger with 18 in. of bare floor at the walls.`);
    facts.push(`Sofa: it reaches 6 in. past each end of a sofa up to ${fmt(l * 12 - 12)} in. long.`);
    const tw = w * 12 - 48;
    const tl = l * 12 - 48;
    if (tw >= 30) facts.push(`Dining: pulled-out chairs stay on it under a table up to ${fmt(tw)} × ${fmt(tl)} in.`);
    const bed = Object.values(BED_RUGS).find((b) => b.size[0] === w && b.size[1] === l);
    if (bed) facts.push(`Bedroom: it is the size our rug guide gives for a ${bed.bed.toLowerCase()} bed, with ${bed.margin}.`);
  } else {
    const td = size.d * 12 - 48;
    if (td < 30) return null;
    facts.push(`Dining: pulled-out chairs stay on it around a round table up to ${fmt(td)} in. across.`);
  }
  return { facts, tools: [TOOLS.rug] };
}

function sofaFacts(title, { w, d, h, seat }) {
  if (!(w > 0 && d > 0 && h > 0)) return null;
  // Taller or deeper than wide: the feed has swapped the axes.
  if (h > w * 1.05 || d > w * 1.05 || !titleAgrees(title, [w, d, h])) return null;
  const narrow = Math.min(d, h);
  const axis = d <= h ? "depth" : "height";
  const facts = [
    `Doorways: it goes through a doorway at least ${fmt(narrow + PATH_CLEARANCE_IN)} in. wide on its back or side (its ${axis} is ${fmt(narrow)} in.).`,
  ];
  const t = coffeeTableSize(w);
  if (t && between(w, 48, 130)) facts.push(`Coffee table: ${t.range[0]}–${t.range[1]} in. long suits its ${fmt(w)} in. length.`);
  if (seat && seat[0] === seat[1] && between(seat[0], 14, 24)) {
    const s = Math.round(seat[0]);
    facts.push(`Coffee table height: ${s - 2}–${s} in., level with or just below its ${fmt(seat[0])} in. seat.`);
  }
  return { facts, tools: [TOOLS.door, TOOLS.coffee] };
}

function coffeeTableFacts(title, { w, d, h }) {
  if (!(w > 0)) return null;
  if (!titleAgrees(title, [w, d, h])) return null;
  const length = Math.max(w, d || 0);
  if (!between(length, 24, 72)) return null;
  const L = Math.round(length);
  const sofas = span(range(40, 160), (s) => {
    const r = coffeeTableSize(s).range;
    return L >= r[0] && L <= r[1];
  });
  const facts = [];
  if (sofas) facts.push(`Sofa: at ${fmt(length)} in. long, it suits a sofa about ${sofas[0]}–${sofas[1]} in. long.`);
  if (h > 0 && between(h, 12, 24) && h < length) {
    const top = Math.round(h);
    facts.push(`Height: its ${fmt(h)} in. top suits a sofa seat ${top}–${top + 2} in. high.`);
  }
  return facts.length ? { facts, tools: [TOOLS.coffee] } : null;
}

const SURFACE_NAME = { counter: "counter", bar: "bar", spectator: "high bar" };
const surfaceLabel = (c) => (c.surface[0] === c.surface[1] ? `${c.surface[0]} in.` : `${c.surface[0]}–${c.surface[1]} in.`);

function stoolFacts(title, { seat }) {
  if (!seat || !(seat[0] > 0)) return null;
  const [lo, hi] = seat;
  // "Adjustable" with a single listed height: the range is unknown, so say nothing.
  if (lo === hi && /\badjustable\b/i.test(title)) return null;
  const classes = STOOL_CLASSES.filter((c) => c.id !== "chair" && Math.min(hi, c.seat[1]) >= Math.max(lo, c.seat[0]));
  if (!classes.length) return null;
  const names = classes.map((c) => `a ${surfaceLabel(c)} ${SURFACE_NAME[c.id]}`);
  const list = names.length > 1 ? `both ${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
  const facts = [
    lo === hi
      ? `Counter height: its ${fmt(lo)} in. seat suits ${list}, leaving the 9–12 in. gap your knees need.`
      : `Counter height: it adjusts from ${fmt(lo)} to ${fmt(hi)} in., so it suits ${list}.`,
  ];
  const style = /\bbackless\b/i.test(title)
    ? "backless"
    : /\b(arms?|armrests?)\b/i.test(title)
      ? "arms"
      : /\b(back|backrest|wingback)\b/i.test(title)
        ? "back"
        : null;
  if (style) {
    const each = STOOL_SPACING_IN[style];
    facts.push(`Spacing: allow ${each} in. of counter for each one, so a 72 in. counter takes ${Math.floor(72 / each)}.`);
  }
  return { facts, tools: [TOOLS.stool] };
}

function chandelierFacts(title, { w, d }) {
  if (!(w > 0)) return null;
  // Round fixtures only: a linear fixture is sized to the table's length instead.
  if (d > 0 && Math.abs(w - d) > 1) return null;
  if (!between(w, 12, 60) || !titleAgrees(title, [w, d])) return null;
  const D = Math.round(w);
  const a = Math.floor(D / 2);
  const facts = [
    `Room: at ${fmt(w)} in. across, it is sized for a room of about ${a} × ${D - a} ft (length plus width in feet ≈ diameter in inches).`,
  ];
  const tables = span(range(24, 60), (tw) => {
    const r = chandelierOverTableIn({ tableWidthIn: tw });
    return D >= r[0] && D <= r[1];
  });
  if (tables && tables[0] <= 48 && !/\bflush\b/i.test(title)) facts.push(`Over a table: it suits a dining table ${tables[0]}–${Math.min(tables[1], 60)} in. wide.`);
  return { facts, tools: [TOOLS.chandelier] };
}

function wallArtFacts(title, { w, h }) {
  if (!(w > 0)) return null;
  // A set's width is per piece or for the group — the feed doesn't say which.
  if (/\b(set of|diptych|triptych)\b/i.test(title)) return null;
  if (!between(w, 24, 80)) return null;
  const W = Math.round(w);
  const furniture = span(range(24, 160), (f) => {
    const r = artWidthOverFurniture(f);
    return W >= r[0] && W <= r[1];
  });
  const facts = [];
  if (furniture) facts.push(`Over furniture: at ${fmt(w)} in. wide, it suits a sofa, bed, console or dresser ${furniture[0]}–${furniture[1]} in. wide.`);
  if (h > 0 && between(h, 8, 80)) {
    facts.push(`On an open wall: hang it with its center 57–60 in. from the floor, which puts the top edge ${Math.round(57 + h / 2)}–${Math.round(60 + h / 2)} in. up.`);
  }
  return facts.length ? { facts, tools: [TOOLS.art] } : null;
}

function diningTableFacts(title, { w, d, h }) {
  if (!(w > 0 && d > 0)) return null;
  // Dining height only: counter and bar tables follow other rules.
  if (!(h > 0 && between(h, 28, 31.5))) return null;
  if (!titleAgrees(title, [w, d, h])) return null;
  const round = /\bround\b/i.test(title) && Math.abs(w - d) <= 2;
  const long = Math.max(w, d);
  const short = Math.min(w, d);
  if (!between(long, 30, 120) || short < 28) return null;
  const facts = [];
  let row = null;
  if (round) {
    row = long >= 36 && long < 42 ? ROUND_TABLES[0] : [...ROUND_TABLES].reverse().find((t) => t.diameter <= long + 1) ?? null;
  } else if (short >= 34) {
    row = [...RECT_TABLES].reverse().find((t) => t.length <= long + 1) ?? null;
  }
  if (row) {
    const [comfy, squeeze] = row.seats;
    facts.push(`Seats: ${comfy} comfortably${squeeze > comfy ? `, ${squeeze} at a squeeze` : ""}, going by our dining table guide.`);
  }
  const roomShort = Math.ceil((short + 2 * CHAIR_CLEARANCE_IN) / 12);
  const roomLong = Math.ceil((long + 2 * CHAIR_CLEARANCE_IN) / 12);
  facts.push(`Room: with ${CHAIR_CLEARANCE_IN} in. for chairs on every side, it needs a room at least ${roomShort} × ${roomLong} ft.`);
  const rug = round ? diningRug({ shape: "round", diameterIn: long }) : diningRug({ shape: "rectangle", lengthIn: long, widthIn: short });
  if (rug?.minimum) {
    facts.push(`Rug: ${round ? `${rug.minimum} ft round` : `${rugLabel(rug.minimum)} ft`} or larger keeps pulled-out chairs on it.`);
  }
  const fixture = chandelierOverTableIn({ tableWidthIn: short });
  if (fixture) facts.push(`Chandelier: ${fixture[0]}–${fixture[1]} in. across, one-half to two-thirds of its width.`);
  return { facts, tools: [TOOLS.dining] };
}

function endTableFacts(title, { w, d, h }) {
  if (!(h > 0 && between(h, 16, 32)) || !titleAgrees(title, [w, d, h])) return null;
  // Not a bar or pub table that landed in the type.
  if (/\b(bar|pub|counter)\b/i.test(title)) return null;
  const t = END_TABLE_ARM_TOLERANCE_IN;
  const top = Math.round(h);
  return {
    facts: [`Arm height: its ${top} in. top suits a sofa or chair arm ${top - t}–${top + t} in. high, within ${t} in. either way.`],
    tools: [GUIDES.endTable],
  };
}

function nightstandFacts(title, { w, d, h }) {
  if (!(h > 0 && between(h, 16, 34)) || !titleAgrees(title, [w, d, h])) return null;
  // Deeper than a nightstand ever is: the feed has swapped the axes.
  if (d > 30) return null;
  const t = NIGHTSTAND_MATTRESS_TOLERANCE_IN;
  const top = Math.round(h);
  return {
    facts: [`Bed height: its ${top} in. top suits a mattress top ${top - t}–${top + t} in. from the floor, within ${t} in. either way.`],
    tools: [GUIDES.nightstand],
  };
}

function mirrorFacts(title, { w, h }) {
  if (!(w > 0)) return null;
  // Floor, full-length and tall narrow mirrors follow other rules; sets are per piece.
  if (/\b(floor|full[- ]length|standing|leaner|set of)\b/i.test(title)) return null;
  if (!between(w, 18, 72) || (h > 0 && h > w * 1.8)) return null;
  const W = Math.round(w);
  const furniture = span(range(18, 120), (f) => {
    const r = artWidthOverFurniture(f);
    return W >= r[0] && W <= r[1];
  });
  if (!furniture) return null;
  return {
    facts: [
      `Over furniture: at ${fmt(w)} in. wide, it suits a dresser, console or vanity ${furniture[0]}–${furniture[1]} in. wide, hung ${MIRROR_GAP_IN[0]}–${MIRROR_GAP_IN[1]} in. above it.`,
    ],
    tools: [GUIDES.mirror],
  };
}

function tableLampFacts(title, { w, d, h }) {
  if (!(h > 0 && between(h, 20, 38)) || !titleAgrees(title, [w, d, h])) return null;
  // Desk and task lamps aim light at a work surface instead.
  if (/\b(desk|task|banker'?s?|piano|clip|uplight)\b/i.test(title)) return null;
  const top = Math.round(h);
  const lo = TABLE_LAMP_TOTAL_IN[0] - top;
  const hi = TABLE_LAMP_TOTAL_IN[1] - top;
  const surfaces = LAMP_SURFACES.filter((s) => Math.min(hi, s.height[1]) - Math.max(lo, s.height[0]) >= 1).map((s) => s.name);
  if (!surfaces.length) return null;
  // A "bedside" lamp the rule puts on a console would contradict its own name.
  if (/\b(bedside|nightstand)\b/i.test(title) && !surfaces.includes("nightstand")) return null;
  const list = surfaces.length > 1 ? `${surfaces.slice(0, -1).join(", ")} or ${surfaces[surfaces.length - 1]}` : surfaces[0];
  const article = /^[aeiou]/i.test(list) ? "an" : "a";
  return {
    facts: [`On a table: at ${top} in. tall, it suits ${article} ${list} about ${lo}–${hi} in. high, so the shade's bottom edge lands near seated eye level.`],
    tools: [GUIDES.tableLamp],
  };
}

/** Bed size from the title; California kings and bunk, loft and trundle pieces are left out. */
function bedSize(title) {
  const t = String(title ?? "");
  if (/\b(california|cal\.?)\s+king\b|\b(bunk|loft|trundle|daybed|headboard|footboard|rails?)\b/i.test(t)) return null;
  if (/\bking\b/i.test(t)) return BED_RUGS.king;
  if (/\bqueen\b/i.test(t)) return BED_RUGS.queen;
  if (/\bfull\b/i.test(t)) return BED_RUGS.full;
  if (/\btwin\b/i.test(t)) return BED_RUGS.twin;
  return null;
}

function bedFacts(title) {
  const bed = bedSize(title);
  if (!bed) return null;
  return {
    facts: [`Rug: ${rugLabel(bed.size)} ft under a ${bed.bed.toLowerCase()} bed leaves ${bed.margin}.`],
    tools: [TOOLS.rug],
  };
}

function overFurnitureFacts(title, { w, d, h }, noun) {
  if (!(w > 0 && between(w, 30, 100)) || !titleAgrees(title, [w, d, h])) return null;
  // Waist-high pieces only: a tall chest has no wall above it to dress.
  if (h > 44) return null;
  // Deeper than wide: the feed has swapped the axes.
  if (d > w * 1.05) return null;
  const [a, b] = artWidthOverFurniture(w);
  return {
    facts: [`Above it: art or a mirror ${a}–${b} in. wide, two-thirds to three-quarters of the ${noun}'s ${fmt(w)} in. width.`],
    tools: [TOOLS.art],
  };
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/**
 * { facts: string[], tools: {href,label}[], intro } for a product, or null when its
 * type has no rule or its sizes are missing or implausible. `title` must be the
 * supplier's raw title (rug size and the leading-size check read it).
 */
export function fitFacts({ productType, title, specs }) {
  const type = String(productType ?? "");
  const s = specs || {};
  let out = null;
  if (type === "Outdoor Rugs" || type === "Area Rugs") out = rugFacts(title);
  else if (type === "Sofas" || type === "Love Seats") out = sofaFacts(title, s);
  else if (type === "Coffee Tables") out = coffeeTableFacts(title, s);
  else if (type === "Bar Chairs") out = stoolFacts(title, s);
  else if (type === "Chandeliers") out = chandelierFacts(title, s);
  else if (type === "Wall Art") out = wallArtFacts(title, s);
  else if (type === "Dining Tables") out = diningTableFacts(title, s);
  else if (type === "End-Side Tables") out = endTableFacts(title, s);
  else if (type === "Nightstands & Bedside Tables") out = nightstandFacts(title, s);
  else if (type === "Mirrors") out = mirrorFacts(title, s);
  else if (type === "Table Lamps") out = tableLampFacts(title, s);
  else if (type === "Standard Beds") out = bedFacts(title);
  else if (type === "Dressers") out = overFurnitureFacts(title, s, "dresser");
  else if (type === "Console Tables") out = overFurnitureFacts(title, s, "console");
  else if (type === "Sideboards") out = overFurnitureFacts(title, s, "sideboard");
  if (!out || !out.facts.length) return null;
  // The link line reads "With your own measurements: <calculator>" or "The rule in full: <guide>".
  out.intro = out.tools.every((t) => t.href.startsWith("/tools/")) ? "With your own measurements:" : "The rule in full:";
  return out;
}
